import {useFormContext, Controller, FieldValues, get} from 'react-hook-form';
import React, {ChangeEvent, SyntheticEvent, useCallback, useState} from 'react';
import {TextField, Autocomplete, Chip} from '@mui/material';
import {isFieldRequired, useValidationContext} from '../../validation/ValidationContext';
import {isFieldInObject} from '../../helpers/json';
import {changeFieldOverride} from '../../utility/FormLayout/layoutConfig';

const FormTagSelect = <T extends FieldValues>(props: T) => {
  const {schema, changeRequest} = useValidationContext();
  const { control, setValue, formState: { errors } } = useFormContext();
  const { name, label, data = [], required = false, fullWidth = true, disabled = false } = props;
  const isRequired = required ? true : schema ? isFieldRequired(schema, name, required) : false;

  let isError = false;
  let errorMessage = '';

  if (errors) {
    errorMessage = get(errors, name)?.message;
    if (errorMessage)
      isError = true;
  }

  const styleForChangeRequest = () => {
    if (changeRequest && isFieldInObject(changeRequest, name)) {
      return changeFieldOverride;
    }
    return undefined;
  }

  const handleChanged = useCallback(
    (e: SyntheticEvent<Element, Event>, selectedVal: string[] | string | boolean | undefined) => {
      if (selectedVal === undefined) setValue(name, null);
      setValue(name, selectedVal);
    },
    [setValue]
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, ...rest } }) => (
        // NOTE: This repeats much of what is in TagSelect but using with react-hook-form Controller
        // makes typing too fiddly to re-use here
        <Autocomplete
          {...rest}
          multiple
          options={data}
          autoComplete
          disabled={disabled}
          onChange={(event, item) => {
            handleChanged(event, item);
          }}
          value={value ? value : []}
          getOptionLabel={(option: any) => option ? option : ''}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth={fullWidth}
              label={label}
              sx={styleForChangeRequest()}
              variant="outlined"
              required={isRequired}
              error={isError}
              helperText={errorMessage}
              disabled={disabled}
            />
          )}
        />
      )}
    />
  )
}

export default FormTagSelect;
